import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { getAudiences } from "@/api";
import { ErrorTextField } from "@/components/atoms";
import { DialogComponentProps, useDemo } from "@/context";
import { useClient } from "@/hooks";
import { Audience } from "@/types";
import { demoClients } from "@/utils";


type CreateAudienceDialogProps = DialogComponentProps<"createAudience">;

const CreateAudienceDialog: React.FC<CreateAudienceDialogProps> = ({
  open,
  onClose,
  onConfirm,
  audienceType
}) => {

  // Global Audiences State
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { getAccessTokenSilently } = useAuth0();
  const client = useClient();
  const { isDemoMode, demoClient } = useDemo();

  // This Audience State
  const [audienceName, setAudienceName] = useState<string>("");
  const [projectName, setProjectName] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);

  // Fetch Audiences - we do this locally rather than use hook to avoid propogating state updates
  useEffect(() => {
    if (!open) return;

    const loadAudiences = async () => {
      setLoading(true);
      try {
        const accessToken = await getAccessTokenSilently();
        const { data } = await getAudiences(
          accessToken,
          audienceType ?? "",
          client,
          isDemoMode ? demoClients.find(x => x.name === demoClient)?.label : undefined
        );
        if (data) {
          setAudiences(data);
        }
      } catch (error: any) {
        // Do nothing
      } finally {
        setLoading(false);
      }
    };
    loadAudiences();

  }, [open, audienceType, client, getAccessTokenSilently, isDemoMode, demoClient]);

  const projects = useMemo(
    () =>
      Array.from(
        new Set(
          audiences
            .filter((a) => a.project_name)
            .map((a) => a.project_name as string)
            .sort((a, b) => a.localeCompare(b)),
        ),
      ),
    [audiences],
  );

  useEffect(() => {
    audiences.find((audience) => audience.audience_name === audienceName)
      ? setError(true)
      : setError(false);
  }, [audienceName, audiences]);

  useEffect(() => {
    if (isDemoMode && demoClient) {
      setProjectName(demoClients.find(x => x.name === demoClient)?.label ?? '')
    }
  }, [isDemoMode, demoClient])

  const handleAudienceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(false);
    setAudienceName(e.target.value);
  };

  const handleProjectNameChange = (
    _event: React.SyntheticEvent,
    value: string | null,
  ) => {
    setProjectName(value);
  };

  const handleProjectInputChange = (
    _event: React.SyntheticEvent,
    value: string,
  ) => {
    setProjectName(value.trim());
  };

  const handleSaveAudience = () => {
    onConfirm(
      audienceName.trim(),
      projectName && projectName !== "" ? projectName.trim() : null,
    );
  };

  return (
    <Dialog open={open} disableEnforceFocus aria-labelledby="save-dialog">
      <DialogTitle>
        <Typography>New Audience</Typography>
      </DialogTitle>
      <DialogContent sx={{ width: 300 }}>
        {loading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" width={252} height={40} />
            <Skeleton variant="rounded" width={252} height={40} />
          </Stack>
        ) : (
        <Stack spacing={2}>
          <Stack>
            <ErrorTextField
              error={error}
              variant="outlined"
              label="Audience Name"
              size="small"
              color="primary"
              placeholder="Audience Name"
              value={audienceName}
              onChange={handleAudienceNameChange}
              sx={{ mt: 1 }}
            />
            {error && (
              <Typography variant="caption" color="error">
                Audience name already exists
              </Typography>
            )}
          </Stack>
          <Autocomplete
            freeSolo
            value={projectName}
            onChange={handleProjectNameChange}
            inputValue={projectName ?? ""}
            onInputChange={handleProjectInputChange}
            options={projects}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : ""
            }
            sx={{ mt: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Project Name (Optional)"
                size="small"
              />
            )}
            disabled={isDemoMode}
          />
        </Stack>
      )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveAudience}
          disabled={error}
        >
          Save Audience
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAudienceDialog;
